// src/pages/InscripcionPublica.tsx
import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  addDoc,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { uploadComprobante } from "../../utils/uploadComprobante";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { QRCodeCanvas } from "qrcode.react";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import FooterTop from "../../components/footer-top";
import Footer from "../../components/footer";
import UserNav from "../../components/navbar/user-nav";
import SEO from "../../components/SEO";
import { Link } from "react-router-dom";

const schema = yup.object({
  taller_doc_id: yup.string().required("Selecciona un taller"),
  nombre: yup.string().required("El nombre es obligatorio"),
  correo: yup.string().email("Correo inválido").required("El correo es obligatorio"),
  telefono: yup.string().required("El teléfono es obligatorio"),
  inmobiliaria: yup.string().required("La inmobiliaria es obligatoria"),
});

export default function InscripcionPublica() {
  const [talleres, setTalleres] = useState<any[]>([]);
  const [qrGenerado, setQrGenerado] = useState<string | null>(null);
  const [comprobante, setComprobante] = useState<File | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    const fetchTalleres = async () => {
      const hoy = new Date();
      const q = query(
        collection(db, "taller"),
        where("publicoObjetivo", "==", "abierto_publico")
      );
      const snapshot = await getDocs(q);
      const getFechaFin = (t: any) =>
        t.fechaTermino?.toDate?.() || t.fecha?.toDate?.() || null;
      const getFechaInicio = (t: any) =>
        t.fechaInicio?.toDate?.() || t.fecha?.toDate?.() || new Date(0);
      const data = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((t: any) => {
          const fin = getFechaFin(t);
          return fin ? fin > hoy : true;
        })
        .sort((a: any, b: any) => getFechaInicio(a).getTime() - getFechaInicio(b).getTime());
      setTalleres(data);
    };
    fetchTalleres();
  }, []);

  const tallerSeleccionadoId = watch("taller_doc_id");
  const tallerSeleccionado = talleres.find((t) => t.id === tallerSeleccionadoId);
  const esPago = tallerSeleccionado?.tipoCosto === "pago";

  const onSubmit = async (data: any) => {
    const { correo, taller_doc_id } = data;

    // Validar duplicado
    const q = query(
      collection(db, "externo_inscripcion"),
      where("correo", "==", correo),
      where("taller_doc_id", "==", taller_doc_id)
    );
    const dupSnap = await getDocs(q);
    if (!dupSnap.empty) {
      Swal.fire({
        icon: "warning",
        title: "Ya estás registrado",
        text: "Este correo ya fue inscrito en el taller seleccionado.",
      });
      return;
    }

    // Si es taller de pago, comprobante es obligatorio
    if (esPago && !comprobante) {
      Swal.fire({
        icon: "warning",
        title: "Comprobante requerido",
        text: "Este evento tiene costo. Adjunta tu comprobante de pago.",
      });
      return;
    }

    // Si no hay duplicado, registrar
    const qrCode = uuidv4();
    const payload = {
      ...data,
      fechaInscripcion: Timestamp.now(),
      completado: false,
      certificado: false,
      codigo_qr: qrCode,
      estatus_pago: "pendiente",
    };

    try {
      const docRef = await addDoc(collection(db, "externo_inscripcion"), payload);

      // Subir comprobante si aplica
      let comprobanteAviso = "";
      if (esPago && comprobante) {
        try {
          const subida = await uploadComprobante(comprobante, taller_doc_id, docRef.id);
          await updateDoc(doc(db, "externo_inscripcion", docRef.id), {
            comprobantePagoUrl: subida.url,
            comprobanteNombre: subida.nombre,
            comprobanteFecha: Timestamp.now(),
          });
        } catch (uploadErr: any) {
          console.warn("Comprobante no subido:", uploadErr);
          comprobanteAviso = " (pero no se pudo subir el comprobante, envíalo al organizador)";
        }
      }

      setQrGenerado(qrCode);
      Swal.fire({
        icon: comprobanteAviso ? "warning" : "success",
        title: comprobanteAviso ? "Registro guardado" : "¡Registro exitoso!",
        text: `Tu inscripción fue registrada${comprobanteAviso}. Guarda el código QR.`,
        confirmButtonText: "Ver QR",
      });
      reset();
      setComprobante(null);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo registrar. Intenta más tarde.",
      });
    }
  };

  return (
    <>
      <SEO
        title="Inscripción a Talleres y Cursos"
        description="Regístrate a talleres y cursos inmobiliarios de AMPI Riviera Nayarit. Capacitación profesional en bienes raíces para la Riviera Nayarit."
        path="/inscripcion-publica"
        keywords="talleres inmobiliarios riviera nayarit, cursos AMPI, capacitación bienes raíces nayarit, inscripción talleres AMPI"
      />
      <UserNav />
      <div className="container mt-5">
        <h1 className="text-center mb-4 h2">Registro a Talleres y Cursos AMPI</h1>

        <div className="alert alert-info text-center">
          ¿Ya tienes cuenta?{" "}
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={() => window.dispatchEvent(new CustomEvent('ampi:open-login'))}
          >
            Inicia sesión aquí para inscribirte como usuario registrado.
          </button>
        </div>

        {talleres.length === 0 ? (
          <p className="text-center">No hay talleres públicos disponibles.</p>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="card p-4">
            <div className="form-group mb-3">
              <label>Taller <span className="text-danger">*</span></label>
              <select className="form-control" {...register("taller_doc_id")}>
                <option value="">Selecciona un taller</option>
                {talleres.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre} – {t.fechaInicio.toDate().toLocaleDateString()}
                  </option>
                ))}
              </select>
              <small className="text-danger">{errors.taller_doc_id?.message}</small>
            </div>

            {/* Banner de datos bancarios (taller de pago) */}
            {esPago && (
              <div className="alert alert-warning mb-3">
                <h6 className="alert-heading"><i className="fa-solid fa-credit-card me-2"></i>Información de pago</h6>
                {tallerSeleccionado.precio != null && (
                  <p className="mb-2">
                    <strong>Costo:</strong> {tallerSeleccionado.precio} {tallerSeleccionado.moneda || "MXN"}
                  </p>
                )}
                {tallerSeleccionado.datosPago ? (
                  <>
                    {tallerSeleccionado.datosPago.banco && <p className="mb-1"><strong>Banco:</strong> {tallerSeleccionado.datosPago.banco}</p>}
                    {tallerSeleccionado.datosPago.titular && <p className="mb-1"><strong>Titular:</strong> {tallerSeleccionado.datosPago.titular}</p>}
                    {tallerSeleccionado.datosPago.cuenta && <p className="mb-1"><strong>Cuenta:</strong> {tallerSeleccionado.datosPago.cuenta}</p>}
                    {tallerSeleccionado.datosPago.clabe && <p className="mb-1"><strong>CLABE:</strong> {tallerSeleccionado.datosPago.clabe}</p>}
                    {tallerSeleccionado.datosPago.referencia && <p className="mb-1"><strong>Referencia sugerida:</strong> {tallerSeleccionado.datosPago.referencia}</p>}
                    {tallerSeleccionado.datosPago.instrucciones && <p className="mb-0 small">{tallerSeleccionado.datosPago.instrucciones}</p>}
                  </>
                ) : (
                  <p className="mb-0 small">Contacta al organizador para los datos de pago.</p>
                )}
              </div>
            )}

            <div className="row">
              <div className="form-group col-md-6">
                <label>Nombre <span className="text-danger">*</span></label>
                <input className="form-control" {...register("nombre")} />
                <small className="text-danger">{errors.nombre?.message}</small>
              </div>
              <div className="form-group col-md-6">
                <label>Correo <span className="text-danger">*</span></label>
                <input className="form-control" {...register("correo")} />
                <small className="text-danger">{errors.correo?.message}</small>
              </div>
              <div className="form-group col-md-6">
                <label>Teléfono <span className="text-danger">*</span></label>
                <input className="form-control" {...register("telefono")} />
                <small className="text-danger">{errors.telefono?.message}</small>
              </div>
              <div className="form-group col-md-6">
                <label>Inmobiliaria <span className="text-danger">*</span></label>
                <input className="form-control" {...register("inmobiliaria")} />
                <small className="text-danger">{errors.inmobiliaria?.message}</small>
              </div>

              {esPago && (
                <div className="form-group col-12 mt-2">
                  <label>Comprobante de pago <span className="text-danger">*</span></label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="form-control"
                    onChange={(e) => setComprobante(e.target.files?.[0] || null)}
                  />
                  <small className="text-muted">JPG, PNG, WEBP o PDF (máx. 5 MB)</small>
                  {comprobante && (
                    <div className="small text-success mt-1">
                      <i className="fa-solid fa-paperclip me-1"></i>{comprobante.name}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary mt-3">
              Inscribirme
            </button>
          </form>
        )}

        {qrGenerado && (
          <div className="text-center mt-4">
            <h5>Tu Código QR:</h5>
            <QRCodeCanvas value={qrGenerado} size={200} />
            <p className="mt-2">Guárdalo para presentarlo el día del evento.</p>
          </div>
        )}
      </div>
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
